import Proposal from '@models/proposal';

import ApiClient from '@/api-client';

const defaultState = {
    proposal: new Proposal(),
    files: [],
    locations: [],
    selectedLocationId: null,
    currentLocation: null,
};

export default {
    namespaced: true,
    state: defaultState,
    getters: {
        isBudgetary(state) {
            return state.proposal.is_budgetary;
        },
        proposalSentToCustomer(state) {
            return state.proposal.status === 'Sent To Customer';
        },
        proposalIsPendingApproval(state) {
            return state.proposal.status === 'Pending Approval';
        },
        proposalWon(state) {
            return state.proposal.status === 'Won';
        },
        proposalSigned(state) {
            return state.proposal.status === 'Signed';
        },
        proposalLost(state) {
            return state.proposal.status === 'Lost';
        },
        proposalChangesNeeded(state) {
            return state.proposal.status === 'Changes Needed';
        },
        proposalEngineeringReview(state) {
            return state.proposal.status === 'Engineering Review';
        },
        proposalInProgress(state) {
            return state.proposal.status === 'In Progress';
        },
        isOnboarding(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('onboarding');
        },
        isBillingAdmin(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('billing_admin');
        },
        isAccountManager(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('account_manager');
        },
        isProposalAuthor(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('proposal_author');
        },
        isEngineer(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('engineer');
        },
        isInsideSales(state, getters, rootState, rootGetters) {
            return rootGetters['AuthenticationStore/roles'].includes('inside_sales');
        },
        isProposalOwner(state, getters, rootState) {
            return state.proposal.owned_by_user_id === rootState.AuthenticationStore.user.id;
        },
        isStatic(state, getters) {
            return getters.proposalSentToCustomer || getters.proposalWon || getters.proposalLost || getters.proposalSigned || getters.proposalIsPendingApproval;
        },
    },
    mutations: {
        setProposal(state, proposal) {
            state.proposal = proposal;
        },
        setProposalLocation(state, locations) {
            state.locations = locations;
        },
        setProposalFiles(state, files) {
            state.files = files;
        },
        setSelectedLocationId(state, locationId) {
            state.selectedLocationId = locationId === null ? null : Number(locationId);
        },
        setCurrentLocation(state, location) {
            state.currentLocation = location;
        },
        updateCurrentLocation(state, updateLocation) {
            state.currentLocation = updateLocation;
        },
        resetState(state) {
            Object.assign(state, defaultState);
        },
    },
    actions: {
        async getProposal({ commit }, proposalId) {
            const proposal = await Proposal
                .with('accountManagers', 'ownedBy', 'engineer', 'termLength', 'approvedByUser', 'jobs', 'billingInformation.file', 'industry', 'customerPurchaseOrders')
                .find(proposalId);

            commit('setProposal', proposal);
        },
        async refreshProposal({ dispatch, state }) {
            await dispatch('getProposal', state.proposal.id);
        },
        async refresh({ dispatch, state, commit }) {
            dispatch('getProposal', state.proposal.id);
            await dispatch('getProposalLocations');

            if (state.currentLocation?.id) {
                const location = state.locations.find((l) => state.currentLocation.id === l.id);

                commit('updateCurrentLocation', location);
            }
        },
        async getProposalLocations({ commit, state }) {
            const locations = await state.proposal.proposalLocations().with(
                'equipment',
                'sdiDiscoverySheet',
                'equipment.item',
                'equipment.vendor',
                'materials',
                'materials.item',
                'materials.frequency',
                'materials.vendor',
                'diServices',
                'diServices.item',
                'diServices.frequency',
                'labor',
                'serviceSchedules',
                'serviceSchedules.frequency',
                'mobileTrailers',
                'branch',
                'membraneRequests',
                'customerLocation',
                'subscriptions',
            ).get();

            commit('setProposalLocation', locations);
        },
        async getFiles({ commit, state }, type = 'internal') {
            const response = await ApiClient.get(`/api/proposals/${state.proposal.id}/files?filter[location]=${type}`);
            commit('setProposalFiles', response.data.data);
        },
        async setSelectedLocationId({ state, commit }, locationId) {
            commit('setSelectedLocationId', locationId);

            const location = state.locations.find((l) => Number(locationId) === l.id) || {};

            if (location) {
                commit('setCurrentLocation', location);
            }
        },
        async reset({ commit }) {
            commit('resetState');
        },
        async navigateToNewProposal({ dispatch }, proposalId) {
            dispatch('reset');
            dispatch('getTripCharges');
            await dispatch('getProposal', proposalId);
            await dispatch('getProposalLocations');
        },
    },
};
